import React, { useCallback, useEffect, useState } from 'react';
import { useGraphQLAuth } from 'hooks/useGraphQLAuth';
import { useToast } from 'hooks/useToast';
import { ACCOUNT_DELETIONS_UPDATED_EVENT } from 'hooks/useAccountDeletionsBadge';
import { Button } from 'components/Button';

export interface AccountDeletionRequest {
  accountDeletionRequestId: string;
  cpf: string;
  reason: string;
  reasonDetails?: string | null;
  origin: string;
  status: 'PENDING' | 'COMPLETED';
  insertDate: string;
  updateDate: string;
}

interface AccountDeletionRowProps {
  cpf: string;
  reason: string;
  reasonDetails: string;
  insertDate: string;
  status: string;
  actions: any;
}

interface Property {
  name: string;
  value: string;
}

const REASON_LABELS: Record<string, string> = {
  NO_LONGER_USE: 'Não usa mais o aplicativo',
  PRIVACY_CONCERNS: 'Preocupações com privacidade',
  TOO_MANY_NOTIFICATIONS: 'Notificações demais',
  BAD_EXPERIENCE: 'Experiência ruim',
  OTHER: 'Outro motivo',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  COMPLETED: 'Concluída',
};

const formatCpf = (cpf: string) =>
  cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');

const formatDate = (date: string) =>
  new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const useAccountDeletionsTable = () => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [requestRows, setRequestRows] = useState<AccountDeletionRowProps[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  /* API CALLS */
  const getRequests = useCallback(async () => {
    setLoading(true);

    const query = `
      query BackofficeAccountDeletionRequests {
        backofficeAccountDeletionRequests {
          accountDeletionRequestId
          cpf
          reason
          reasonDetails
          origin
          status
          insertDate
          updateDate
        }
      }
    `;

    try {
      const response = await connectGraphQLApi({
        query,
        errorMessage: 'Falha ao listar solicitações de exclusão',
        useNewApi: true,
      });

      setError(false);
      const responseData = (response as any).data;
      const requests: AccountDeletionRequest[] =
        responseData.backofficeAccountDeletionRequests || [];

      const pendingCount = requests.filter(
        (request) => request.status === 'PENDING'
      ).length;

      setProperties([
        { name: 'Total de solicitações', value: requests.length.toString() },
        { name: 'Pendentes', value: pendingCount.toString() },
        {
          name: 'Concluídas',
          value: (requests.length - pendingCount).toString(),
        },
      ]);

      const rows = requests.map((request) => ({
        cpf: formatCpf(request.cpf),
        reason: REASON_LABELS[request.reason] ?? request.reason,
        reasonDetails: request.reasonDetails || '—',
        insertDate: formatDate(request.insertDate),
        status: STATUS_LABELS[request.status] ?? request.status,
        actions:
          request.status === 'PENDING'
            ? [
                <Button
                  key="complete"
                  label="Concluir"
                  size="small"
                  typeBtn="blue"
                  onClick={() =>
                    completeRequest(request.accountDeletionRequestId)
                  }
                  title="Marcar solicitação como concluída"
                />,
              ]
            : '—',
      }));

      setRequestRows(rows);
      setLoading(false);
    } catch (error: any) {
      console.error('Error details:', error);
      setError(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeRequest = async (accountDeletionRequestId: string) => {
    setCompletingId(accountDeletionRequestId);

    const query = `
      mutation BackofficeCompleteAccountDeletionRequest($accountDeletionRequestId: ID!) {
        backofficeCompleteAccountDeletionRequest(accountDeletionRequestId: $accountDeletionRequestId) {
          accountDeletionRequestId
          status
        }
      }
    `;

    try {
      await connectGraphQLApi({
        query,
        variables: { accountDeletionRequestId },
        errorMessage: 'Falha ao concluir solicitação',
        useNewApi: true,
      });

      setToast({
        type: 'success',
        message: 'Solicitação marcada como concluída',
        isActive: true,
        timeToRemoveToast: 3000,
      });

      // Refresh the sidebar badge and the table
      window.dispatchEvent(new Event(ACCOUNT_DELETIONS_UPDATED_EVENT));
      setRefreshKey((key) => key + 1);
    } catch (error: any) {
      console.error('Error details:', error);
    } finally {
      setCompletingId(null);
    }
  };

  useEffect(() => {
    getRequests();
  }, [getRequests, refreshKey]);

  return {
    loading,
    error,
    completingId,
    properties,
    requestRows,
  };
};
